import React, { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { makeStyles } from 'tss-react/mui';
import { isEnabledFromState, isSupported as isAvModerationSupported } from '../../../av-moderation/functions';
import Avatar from '../../../base/avatar/components/Avatar';
import { isMobileBrowser } from '../../../base/environment/utils';
import { MEDIA_TYPE } from '../../../base/media/constants';
import { PARTICIPANT_ROLE } from '../../../base/participants/constants';
import { getLocalParticipant, getParticipantCount, isParticipantModerator } from '../../../base/participants/functions';
import { isParticipantAudioMuted, isParticipantVideoMuted } from '../../../base/tracks/functions.any';
import ContextMenu from '../../../base/ui/components/web/ContextMenu';
import ContextMenuItemGroup from '../../../base/ui/components/web/ContextMenuItemGroup';
import { getBreakoutRooms, getCurrentRoomId, isInBreakoutRoom } from '../../../breakout-rooms/functions';
import { displayVerification } from '../../../e2ee/functions';
import { setVolume } from '../../../filmstrip/actions.web';
import { QUICK_ACTION_BUTTON } from '../../../participants-pane/constants';
import { getQuickActionButtonType, isForceMuted } from '../../../participants-pane/functions';
import { requestRemoteControl, stopController } from '../../../remote-control/actions';
import { getParticipantMenuButtonsWithNotifyClick, showOverflowDrawer } from '../../../toolbox/functions.web';
import { NOTIFY_CLICK_MODE } from '../../../toolbox/types';
import { getVisitorsCount, iAmVisitor } from '../../../visitors/functions';
import { PARTICIPANT_MENU_BUTTONS as BUTTONS } from '../../constants';
import AskToUnmuteButton from './AskToUnmuteButton';
import CustomOptionButton from './CustomOptionButton';
import DemoteToVisitorButton from './DemoteToVisitorButton';
import GrantModeratorButton from './GrantModeratorButton';
import KickButton from './KickButton';
import MuteButton from './MuteButton';
import MuteEveryoneElseButton from './MuteEveryoneElseButton';
import MuteEveryoneElsesVideoButton from './MuteEveryoneElsesVideoButton';
import MuteVideoButton from './MuteVideoButton';
import PrivateMessageMenuButton from './PrivateMessageMenuButton';
import RemoteControlButton, { REMOTE_CONTROL_MENU_STATES } from './RemoteControlButton';
import SendToRoomButton from './SendToRoomButton';
import TogglePinToStageButton from './TogglePinToStageButton';
import VerifyParticipantButton from './VerifyParticipantButton';
import VolumeSlider from './VolumeSlider';
import VolumeSliderMobile from "./VolumeSliderMobile";
import { isNeedShowElectronOnlyElements } from "../../../base/environment/utils_web";
import { getWindowQueryData } from "../../../desktop-picker/functions";
const useStyles = makeStyles()(theme => {
    return {
        contextMenu: {
            '& .context-menu-item-group:empty + div': {
                display: "none",
            }
        },
        participantInfoContainer: {
            display: 'flex',
            background: 'rgba(25, 25, 25, 1)',
            padding: '14px 16px',
            gap: '8px',
        },
        participantInfoAvatar: {},
        participantInfoNameRoleContainer: {
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
        },
        participantInfoName: {
            fontFamily: 'Lato Bold',
            fontWeight: 'normal',
            fontSize: '16px',
            lineHeight: '20px',
            color: 'rgba(255, 255, 255, 0.85)',
        },
        participantInfoRole: {
            fontFamily: 'Lato Regular',
            fontWeight: 'normal',
            fontSize: '14px',
            lineHeight: '20px',
            color: 'rgba(255, 255, 255, 0.3)',
        },
        contextItem: {
            padding: '8px 12px 6px 12px',
        },
        contextItemMobile: {
            '& .jitsi-icon': {
                '& svg': {
                    fill: 'rgba(255, 255, 255, 0.3) !important'
                },
            },
        },
        separateLineContainer: {
            paddingTop: '6px',
            paddingBottom: '7px',
            paddingLeft: '24px',
            paddingRight: '24px'
        },
        separateLineContainerPaddingX: {
            paddingLeft: '24px',
            paddingRight: '24px'
        },
        separateLine: {
            backgroundColor: 'rgba(255, 255, 255, 0.05)',
            height: '1px'
        },
        separateLineContainerMobile: {
            padding: '0px 18px',
        },
        separateLineMobile: {
            height: '1px',
            backgroundImage: "url(\"data:image/svg+xml,%3csvg width='100%25' height='100%25' xmlns='http://www.w3.org/2000/svg'%3e%3crect width='100%25' height='100%25' fill='none' stroke='%23FFFFFF1A' stroke-width='4' stroke-dasharray='1%2c 16' stroke-dashoffset='0' stroke-linecap='square'/%3e%3c/svg%3e\")",
        },
        text: {
            color: theme.palette.text02,
            padding: '10px 16px',
            height: '40px',
            overflow: 'hidden',
            display: 'flex',
            alignItems: 'center',
            boxSizing: 'border-box'
        }
    };
});
const ParticipantContextMenu = ({ className, closeDrawer, drawerParticipant, offsetTarget, onEnter, onLeave, onSelect, participant, remoteControlState, thumbnailMenu }) => {
    const dispatch = useDispatch();
    const { t } = useTranslation();
    const { classes: styles, cx } = useStyles();
    const localParticipant = useSelector(getLocalParticipant);
    const _isModerator = Boolean(localParticipant?.role === PARTICIPANT_ROLE.MODERATOR);
    const _isVideoForceMuted = useSelector(state => isForceMuted(participant, MEDIA_TYPE.VIDEO, state));
    const _isAudioMuted = useSelector((state) => isParticipantAudioMuted(participant, state));
    const _isVideoMuted = useSelector((state) => isParticipantVideoMuted(participant, state));
    const _overflowDrawer = useSelector(showOverflowDrawer);
    const { remoteVideoMenu = {}, disableRemoteMute, startSilent, customParticipantMenuButtons } = useSelector((state) => state['features/base/config']);
    const visitorsMode = useSelector((state) => iAmVisitor(state));
    const visitorsSupported = useSelector((state) => state['features/visitors'].supported);
    const visitorsCount = useSelector(getVisitorsCount);
    const { disableDemote, disableKick, disableGrantModerator, disablePrivateChat } = remoteVideoMenu;
    const { lobbyEnabled } = useSelector((state) => state['features/lobby']);
    const { participantsVolume } = useSelector((state) => state['features/filmstrip']);
    const _volume = (participant?.local ?? true ? undefined
        : participant?.id ? participantsVolume[participant?.id] : undefined) ?? 1;
    const isBreakoutRoom = useSelector(isInBreakoutRoom);
    const isModerationSupported = useSelector((state) => isAvModerationSupported()(state));
    const shouldDisplayVerification = useSelector((state) => displayVerification(state, participant?.id));
    const buttonsWithNotifyClick = useSelector(getParticipantMenuButtonsWithNotifyClick);
    const isMobile = isMobileBrowser();
    const { isWebVersion } = getWindowQueryData();
    const isNeedAddAction = isNeedShowElectronOnlyElements() || isWebVersion;
    const isEnabledVideoFromState = useSelector((state) => isEnabledFromState(MEDIA_TYPE.VIDEO, state));
    const isEnabledAudioModerationFromState = useSelector((state) => isEnabledFromState(MEDIA_TYPE.AUDIO, state));
    const participantCount = useSelector(getParticipantCount);
    const _currentRoomId = useSelector(getCurrentRoomId);
    const _rooms = Object.values(useSelector(getBreakoutRooms));
    const _onVolumeChange = useCallback(value => {
        dispatch(setVolume(participant.id, value));
    }, [setVolume, dispatch]);
    const _getCurrentParticipantId = useCallback(() => {
        const drawer = _overflowDrawer && !thumbnailMenu;
        return (drawer ? drawerParticipant?.participantID : participant?.id) ?? '';
    }, [thumbnailMenu, _overflowDrawer, drawerParticipant, participant]);
    const notifyClick = useCallback((buttonKey) => {
        const notifyMode = buttonsWithNotifyClick?.get(buttonKey);
        if (!notifyMode) {
            return;
        }
        APP.API.notifyParticipantMenuButtonClicked(buttonKey, _getCurrentParticipantId(), notifyMode === NOTIFY_CLICK_MODE.PREVENT_AND_NOTIFY);
    }, [buttonsWithNotifyClick, _getCurrentParticipantId]);
    const onBreakoutRoomButtonClick = useCallback(() => {
        onSelect(true);
    }, [onSelect]);
    const isClickedFromParticipantPane = useMemo(() => !_overflowDrawer && !thumbnailMenu, [_overflowDrawer, thumbnailMenu]);
    const quickActionButtonType = useSelector((state) => getQuickActionButtonType(participant, _isAudioMuted, _isVideoMuted, state));
    const buttons = [];
    const buttons2 = [];
    const buttons3 = [];
    const showVolumeSlider = !startSilent
        && !isMobileBrowser()
        && (_overflowDrawer || thumbnailMenu)
        && typeof _volume === 'number'
        && !isNaN(_volume);
    const getButtonProps = useCallback((key) => {
        const notifyMode = buttonsWithNotifyClick?.get(key);
        const shouldNotifyClick = typeof notifyMode !== 'undefined';
        const notifyClickCallback = (key) => {
            notifyClick(key);
            onLeave?.();
        };
        return {
            key,
            notifyMode,
            notifyClick: shouldNotifyClick ? () => notifyClickCallback(key) : onLeave,
            participantID: _getCurrentParticipantId()
        };
    }, [_getCurrentParticipantId, buttonsWithNotifyClick, notifyClick]);
    if (_isModerator && isNeedAddAction) {
        if (isModerationSupported) {
            if (_isAudioMuted && !participant.isSilent
                && !(isClickedFromParticipantPane && quickActionButtonType === QUICK_ACTION_BUTTON.ASK_TO_UNMUTE)) {
                buttons.push(React.createElement(AskToUnmuteButton, { ...getButtonProps(BUTTONS.ASK_UNMUTE), className: styles.contextItem, buttonType: MEDIA_TYPE.AUDIO }));
            }
            if (_isVideoForceMuted
                && !(isClickedFromParticipantPane && quickActionButtonType === QUICK_ACTION_BUTTON.ALLOW_VIDEO)) {
                buttons.push(React.createElement(AskToUnmuteButton, { ...getButtonProps(BUTTONS.ALLOW_VIDEO), className: styles.contextItem, buttonType: MEDIA_TYPE.VIDEO }));
            }
        }
        if (!disableRemoteMute && !participant.isSilent) {
            if (!(isClickedFromParticipantPane && quickActionButtonType === QUICK_ACTION_BUTTON.MUTE)) {
                buttons.push(React.createElement(MuteButton, { ...getButtonProps(BUTTONS.MUTE), className: styles.contextItem }));
            }
            if (participantCount > 2) {
                buttons.push(React.createElement(MuteEveryoneElseButton, { isEnabledAudioModerationFromState: isEnabledAudioModerationFromState, ...getButtonProps(BUTTONS.MUTE_OTHERS), className: styles.contextItem }));
            }
            if (!(isClickedFromParticipantPane && quickActionButtonType === QUICK_ACTION_BUTTON.STOP_VIDEO)) {
                buttons.push(React.createElement(MuteVideoButton, { ...getButtonProps(BUTTONS.MUTE_VIDEO), className: styles.contextItem, text: t('participantsPane.actions.stopVideoFull') }));
            }
            if (participantCount > 2) {
                buttons.push(React.createElement(MuteEveryoneElsesVideoButton, { isEnabledVideoFromState: isEnabledVideoFromState, ...getButtonProps(BUTTONS.MUTE_OTHERS_VIDEO), className: styles.contextItem }));
            }
        }
        if (!disableGrantModerator && !isBreakoutRoom) {
            buttons2.push(React.createElement(GrantModeratorButton, { ...getButtonProps(BUTTONS.GRANT_MODERATOR), className: styles.contextItem }));
        }
        if (!disableDemote && visitorsSupported && _isModerator && !lobbyEnabled && visitorsCount > 0) {
            buttons2.push(React.createElement(DemoteToVisitorButton, { ...getButtonProps(BUTTONS.DEMOTE), className: styles.contextItem }));
        }
        if (shouldDisplayVerification) {
            buttons2.push(React.createElement(VerifyParticipantButton, { ...getButtonProps(BUTTONS.VERIFY), className: styles.contextItem }));
        }
    }
    if (!disablePrivateChat && !visitorsMode) {
        buttons2.push(React.createElement(PrivateMessageMenuButton, { ...getButtonProps(BUTTONS.PRIVATE_MESSAGE), className: styles.contextItem }));
    }
    buttons2.push(React.createElement(TogglePinToStageButton, { ...getButtonProps(BUTTONS.PIN_TO_STAGE), className: cx(styles.contextItem, isMobile && styles.contextItemMobile) }));
    if (_isModerator && isNeedAddAction) {
        if (!disableKick) {
            if (isClickedFromParticipantPane) {
                buttons2.push(React.createElement(KickButton, { ...getButtonProps(BUTTONS.KICK), className: styles.contextItem }));
            }
            else {
                buttons3.push(React.createElement(KickButton, { ...getButtonProps(BUTTONS.KICK), className: styles.contextItem }));
            }
        }
    }
    if (thumbnailMenu && remoteControlState) {
        const onRemoteControlToggle = useCallback(() => {
            if (remoteControlState === REMOTE_CONTROL_MENU_STATES.STARTED) {
                dispatch(stopController(true));
            }
            else if (remoteControlState === REMOTE_CONTROL_MENU_STATES.NOT_STARTED) {
                dispatch(requestRemoteControl(_getCurrentParticipantId()));
            }
        }, [dispatch, remoteControlState, stopController, requestRemoteControl]);
        buttons2.push(React.createElement(RemoteControlButton, { ...getButtonProps(BUTTONS.REMOTE_CONTROL), className: styles.contextItem, onClick: onRemoteControlToggle, remoteControlState: remoteControlState }));
    }
    if (customParticipantMenuButtons) {
        customParticipantMenuButtons.forEach(({ icon, id, text }) => {
            buttons2.push(React.createElement(CustomOptionButton, { icon: icon, key: id, 
                // eslint-disable-next-line react/jsx-no-bind
                onClick: () => notifyClick(id), className: styles.contextItem, text: text }));
        });
    }
    const breakoutRoomsButtons = [];
    if (!thumbnailMenu && _isModerator && isNeedAddAction) {
        _rooms.forEach(room => {
            if (room.id !== _currentRoomId) {
                breakoutRoomsButtons.push(React.createElement(SendToRoomButton, { ...getButtonProps(BUTTONS.SEND_PARTICIPANT_TO_ROOM), key: room.id, onClick: onBreakoutRoomButtonClick, className: styles.contextItem, room: room }));
            }
        });
    }
    return (React.createElement(ContextMenu, { className: `${styles.contextMenu} ${className}`, entity: participant, hidden: thumbnailMenu ? false : undefined, inDrawer: thumbnailMenu && _overflowDrawer, isDrawerOpen: Boolean(drawerParticipant), offsetTarget: offsetTarget, width: 282, onClick: onSelect, onDrawerClose: thumbnailMenu ? onSelect : closeDrawer, onMouseEnter: onEnter, onMouseLeave: onLeave },
        isMobile && (React.createElement("div", { className: styles.participantInfoContainer },
            React.createElement("div", { className: styles.participantInfoAvatar },
                React.createElement(Avatar, { id: "participantInfoAvatar", participantId: participant.id, size: 44 })),
            React.createElement("div", { className: styles.participantInfoNameRoleContainer },
                React.createElement("div", { className: styles.participantInfoName }, participant.name),
                React.createElement("div", { className: styles.participantInfoRole }, isParticipantModerator(participant) ? t('videothumbnail.moderator') : t('videothumbnail.member'))))),
        !thumbnailMenu && _overflowDrawer && !isMobile && drawerParticipant && React.createElement(ContextMenuItemGroup, { actions: [{
                    accessibilityLabel: drawerParticipant.displayName,
                    customIcon: React.createElement(Avatar, { participantId: drawerParticipant.participantID, size: 20 }),
                    text: drawerParticipant.displayName
                }] }),
        showVolumeSlider && (isMobile ? (React.createElement(React.Fragment, null,
            React.createElement(ContextMenuItemGroup, null,
                React.createElement(VolumeSliderMobile, { initialValue: _volume, key: 'volume-slider', onChange: _onVolumeChange })),
            React.createElement("div", { className: styles.separateLineContainerMobile },
                React.createElement("div", { className: styles.separateLineMobile })))) : (React.createElement(React.Fragment, null,
            React.createElement(ContextMenuItemGroup, null,
                React.createElement(VolumeSlider, { initialValue: _volume, key: 'volume-slider', onChange: _onVolumeChange })),
            React.createElement("div", { className: cx(styles.separateLineContainer, !isClickedFromParticipantPane && styles.separateLineContainerPaddingX) },
                React.createElement("div", { className: styles.separateLine }))))),
        isMobile ? (React.createElement(ContextMenuItemGroup, null,
            buttons.length > 0 && buttons,
            buttons2,
            buttons3.length > 0 && buttons3)) : (React.createElement(React.Fragment, null,
            buttons.length > 0 && (React.createElement(React.Fragment, null,
                React.createElement(ContextMenuItemGroup, null, buttons),
                React.createElement("div", { className: cx(styles.separateLineContainer, !isClickedFromParticipantPane && styles.separateLineContainerPaddingX) },
                    React.createElement("div", { className: styles.separateLine })))),
            React.createElement(ContextMenuItemGroup, null, buttons2),
            buttons3.length > 0 && (React.createElement(React.Fragment, null,
                React.createElement("div", { className: cx(styles.separateLineContainer, !isClickedFromParticipantPane && styles.separateLineContainerPaddingX) },
                    React.createElement("div", { className: styles.separateLine })),
                React.createElement(ContextMenuItemGroup, null, buttons3))))),
        breakoutRoomsButtons.length > 0 && (React.createElement(ContextMenuItemGroup, null,
            React.createElement("div", { className: styles.text }, t('breakoutRooms.actions.sendToBreakoutRoom')),
            breakoutRoomsButtons))));
};
export default ParticipantContextMenu;
